/** 共通レイアウト：ヘッダ（固定64px）/ main / フッタ（UI/UX設計書 §7.1・§12.2 ランドマーク） */
import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { COMPANY_URL, COPYRIGHT, PUBLIC_MODE, PUBLIC_TOP_PATH, SITE_NAME } from "../lib/publicMode";
import { Badge, Button } from "./ui";

function CartBadge({ count }: { count: number }) {
  return count > 0 ? <Badge tone="accent">{count}</Badge> : null;
}

export default function Layout() {
  const { user, logout, cartCount, unreadCount, refreshBadges } = useAuth();
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  // "/" で検索フォーカス（§12.3 キーボードショートカット）
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (e.key === "/" && tag !== "INPUT" && tag !== "TEXTAREA") {
        e.preventDefault();
        document.getElementById("global-search")?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => { void refreshBadges(); }, [refreshBadges]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    navigate(`/products?keyword=${encodeURIComponent(keyword)}`);
  };

  return (
    <>
      <a className="skip-link" href="#main">本文へスキップ</a>
      <header
        style={{
          position: "sticky", top: 0, zIndex: 40, height: "var(--header-height)",
          background: "var(--surface-default)", boxShadow: "var(--elev-1)",
        }}
      >
        <div className="container row app-header" style={{ height: "100%" }}>
          <Link
            to={PUBLIC_MODE ? PUBLIC_TOP_PATH : "/"}
            style={{ font: "var(--font-h3)", color: "var(--color-primary)", textDecoration: "none" }}
          >
            TF&#8209;Mall
          </Link>
          {PUBLIC_MODE ? (
            <span className="spacer muted" style={{ marginLeft: "var(--space-4)", font: "var(--font-small)" }}>
              3D 仮想空間ショッピングモール（研究公開版）
            </span>
          ) : (
          <>
          <nav className="row header-nav" aria-label="メイン" style={{ marginLeft: "var(--space-4)" }}>
            <NavLink to="/products" className="btn btn--ghost">商品</NavLink>
            <NavLink to="/spaces" className="btn btn--ghost">3D モール</NavLink>
          </nav>
          <form className="spacer header-search" role="search" onSubmit={submit} style={{ maxWidth: 420, margin: "0 var(--space-4)" }}>
            <label className="sr-only" htmlFor="global-search">商品を検索</label>
            <input
              id="global-search" className="input" type="search" placeholder="商品を検索（/ でフォーカス）"
              value={keyword} onChange={(e) => setKeyword(e.target.value)}
              style={{ minHeight: 40 }}
            />
          </form>
          <div className="row">
            {user ? (
              <>
                <NavLink to="/notices" className="btn btn--ghost" aria-label={`通知${unreadCount ? `（未読 ${unreadCount} 件）` : ""}`}>
                  通知 {unreadCount > 0 && <Badge tone="accent">{unreadCount}</Badge>}
                </NavLink>
                <NavLink to="/cart" className="btn btn--ghost" aria-label={`カート（${cartCount} 点）`}>
                  カート <CartBadge count={cartCount} />
                </NavLink>
                {user.role === "shop_owner" && <NavLink to="/shop" className="btn btn--ghost header-nav">店舗管理</NavLink>}
                {user.role === "admin" && <NavLink to="/admin" className="btn btn--ghost header-nav">管理</NavLink>}
                <NavLink to="/mypage" className="btn btn--ghost header-user" title={user.display_name}>{user.display_name}</NavLink>
                <Button variant="ghost" className="header-nav" onClick={() => { void logout(); navigate("/"); }}>ログアウト</Button>
              </>
            ) : (
              <>
                <NavLink to="/cart" className="btn btn--ghost">カート</NavLink>
                <NavLink to="/login" className="btn btn--secondary btn--sm">ログイン</NavLink>
                <NavLink to="/register" className="btn btn--primary btn--sm">会員登録</NavLink>
              </>
            )}
          </div>
          </>
          )}
        </div>
      </header>

      <main id="main" style={{ minHeight: "calc(100vh - var(--header-height) - 180px)", padding: "var(--space-6) 0" }}>
        <Outlet />
      </main>

      <footer
        style={{
          background: "var(--surface-default)",
          borderTop: "1px solid var(--border-subtle)",
          // 公開モードは 3D が画面いっぱいなので、フッタは薄く固定の高さにして常に見えるようにする
          padding: PUBLIC_MODE ? "var(--space-2) 0" : "var(--space-6) 0",
          height: PUBLIC_MODE ? "var(--public-footer-height)" : undefined,
          display: PUBLIC_MODE ? "grid" : undefined,
          placeItems: PUBLIC_MODE ? "center" : undefined,
        }}
      >
        {PUBLIC_MODE ? (
          <div className="container" style={{ textAlign: "center" }}>
            <a
              href={COMPANY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="muted"
              style={{ font: "var(--font-small)" }}
            >
              {COPYRIGHT}
            </a>
          </div>
        ) : (
          <div className="container stack">
            <nav className="row" aria-label="フッタ" style={{ flexWrap: "wrap" }}>
              <Link to="/legal/terms">利用規約</Link>
              <Link to="/legal/privacy">プライバシーポリシー</Link>
              <Link to="/legal/tokushoho">特定商取引法に基づく表記</Link>
              <Link to="/legal/contact">お問い合わせ</Link>
            </nav>
            <p className="muted">© 2026 {SITE_NAME} — 仮想空間ショッピングモール（ローカル開発環境）</p>
          </div>
        )}
      </footer>

      {/* §11：xs / sm ではヘッダを簡略化し、検索と補助ナビを畳む */}
      <style>{`
        @media (max-width: 767px) {
          .app-header { gap: var(--space-1); }
          .app-header .header-search { display: none; }
          .app-header .header-nav { display: none; }
          .app-header .btn { padding: 0 var(--space-2); font-size: 0.875rem; }
          .app-header .header-user {
            max-width: 7em; overflow: hidden; text-overflow: ellipsis;
            white-space: nowrap; display: inline-block; line-height: 44px;
          }
        }
        @media (max-width: 1023px) {
          .app-header .header-search { max-width: 220px !important; margin: 0 var(--space-2) !important; }
        }
      `}</style>
    </>
  );
}
